import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Users, HeartPulse, Home as HomeIcon, ShieldAlert, Compass, GraduationCap } from "lucide-react";
import { SERVICE_BY_SLUG } from "../../data/publicSite";
import usePageMeta from "../../lib/usePageMeta";
import AccessModelCallout from "../../components/public/AccessModelCallout";

const ICON_MAP = { Users, HeartPulse, Home: HomeIcon, ShieldAlert, Compass, GraduationCap };

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICE_BY_SLUG[slug];
  usePageMeta({
    title: service?.metaTitle || (service ? `${service.title} | California Corporate Chaplains` : "Services | California Corporate Chaplains"),
    description: service?.metaDescription || service?.description || service?.summary,
  });
  if (!service) return <Navigate to="/services" replace />;

  const Icon = ICON_MAP[service.icon] || Users;
  const ctaLabel = service.cta || "Request a Consultation";

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid={`service-detail-${service.slug}`}>
      <Link to="/services" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8">
        <ArrowLeft className="w-4 h-4" /> All services
      </Link>

      <div className="flex items-start gap-4 mb-6">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#F0F4F8] text-[#4A6FA5] shrink-0">
          <Icon className="w-6 h-6" strokeWidth={1.75} />
        </span>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-1">Service</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">{service.title}</h1>
        </div>
      </div>

      <p className="text-lg text-slate-700 leading-relaxed">{service.tagline}</p>

      {service.heroImage && (
        <div
          className="mt-8 aspect-[16/9] rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-100"
          data-testid={`service-hero-image-${service.slug}`}
        >
          <img
            src={service.heroImage.src}
            alt={service.heroImage.alt}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">What's included</p>
        <ul className="grid sm:grid-cols-2 gap-3">
          {service.bullets.map((b) => (
            <li key={b} className="flex items-start gap-3 text-slate-700">
              <Check className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={2} />
              <span className="leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-10 max-w-none">
        {service.body.split("\n\n").map((para, i) => (
          <p key={i} className="text-base text-slate-700 leading-relaxed mb-4 last:mb-0">
            {para}
          </p>
        ))}
      </div>

      {service.settings && service.settings.length > 0 && (
        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm" data-testid={`service-settings-${service.slug}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Settings represented</p>
          <ul className="grid sm:grid-cols-2 gap-3">
            {service.settings.map((s) => (
              <li key={s} className="flex items-start gap-3 text-slate-700">
                <Check className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={2} />
                <span className="leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {service.audiences && service.audiences.length > 0 && (
        <div className="mt-10" data-testid={`service-audiences-${service.slug}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Who CCCHAPS may serve in this setting</p>
          {service.audienceImage && (
            <div
              className="mb-6 aspect-[16/9] rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-100"
              data-testid={`service-audience-image-${service.slug}`}
            >
              <img
                src={service.audienceImage.src}
                alt={service.audienceImage.alt}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-5">
            {service.audiences.map((a) => (
              <article key={a.label} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="font-heading text-lg text-slate-900">{a.label}</h3>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed">{a.body}</p>
              </article>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500 leading-relaxed">
            Resident and family support is voluntary, permission-based, respectful of belief and non-belief, and non-clinical. CCCHAPS complements — it does not replace — facility clinical services, medical care, mental-health treatment, social work, hospice services, facility staff chaplains, residents' existing clergy or faith communities, or emergency services.
          </p>
        </div>
      )}

      {service.clientCallouts && service.clientCallouts.length > 0 && (
        <div className="mt-10 grid md:grid-cols-2 gap-5" data-testid={`service-client-callouts-${service.slug}`}>
          {service.clientCallouts.map((c) => (
            <div key={c.label} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-2">{c.label}</p>
              <p className="text-sm text-slate-700 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      )}

      {(service.slug === "senior-living" || service.slug === "crisis-grief-bereavement") && (
        <AccessModelCallout
          variant={service.slug === "senior-living" ? "senior-living" : "default"}
          className="mt-10"
          testId={`sd-access-model-callout-${service.slug}`}
        />
      )}

      <div className="mt-12 rounded-2xl bg-[#F0F4F8] border border-blue-100 p-6 md:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="font-heading text-xl font-medium text-slate-900">
            {service.ctaLead?.title || "Ready to talk?"}
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            {service.ctaLead?.body || "Confidential consultation — no pitch, no obligation."}
          </p>
        </div>
        <Link
          to={`/schedule?type=${service.slug === "workplace-chaplaincy" ? "workplace_chaplaincy" : service.slug === "healthcare-hospice" ? "healthcare_hospice" : service.slug === "senior-living" ? "senior_living" : service.slug === "training-workshops" ? "training_workshop" : service.slug === "leadership-employee-support" ? "employee_support" : "organizational_consultation"}`}
          data-testid={`service-cta-${service.slug}`}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] whitespace-nowrap"
        >
          {ctaLabel} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {service.safetyNote && (
        <p
          className="mt-6 text-xs text-slate-500 leading-relaxed border-l-2 border-slate-200 pl-4"
          data-testid={`service-safety-note-${service.slug}`}
        >
          {service.safetyNote}
        </p>
      )}
    </article>
  );
}
